/**
 * Settings-driven portal: wording, feature switches and limits come from
 * `GET api/rides/lookups → settings` (RidesPublicSettingsView). Everything here has a
 * complete typed default so the UI renders before — or without — the API, and the
 * defaults mirror the seed in `_dbMigration2PostgrSQL/pg/ridesmanagement/254c_RidesManagement_settings_pg.sql`
 * (+ 254f photos, 254g documents / driver standards).
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RIDES_PROVIDERS, RIDES_REQUIREMENT_LEVEL, useRidesLookupsService, type RidesPublicSettingsView } from '@actschurch/ccms-sdk';
import { ridesKeys } from './queryKeys';

// ---------------------------------------------------------------- default copy

/**
 * Public copy keys (RideCopy rows with isPublic = true). Keep the keys AND the default text
 * identical to the seed so a portal without the API shows exactly what the seed would.
 * `{name}` placeholders are replaced by `t(key, fallback, vars)`.
 */
export const DEFAULT_COPY = {
  'app.name': 'Acts Church Carpool',
  'app.tagline': 'Verified carpooling',
  'landing.eyebrow': "Get where you're going, together",
  'landing.headline': 'Shared rides to church, with people you can trust.',
  'landing.intro': 'Every driver and passenger has an ID check before they ride. Pick-ups are only at official Acts Church campuses. How would you like to start?',
  'landing.rider_card_title': 'I need a ride',
  'landing.rider_card_text': 'Find a verified driver heading to a service or event from your campus.',
  'landing.driver_card_title': 'I can drive',
  'landing.driver_card_text': 'Offer your spare seats. We check your ID and licence once, then you can offer lifts.',
  'landing.step1': 'Choose the service or event, then your pick-up and drop-off campuses.',
  'landing.step2': 'Do your ID check once. Campus security confirms it in person.',
  'landing.step3': "Book a seat. We'll let you know when your driver arrives.",
  'consent.data_text': "I've read the privacy notice and I consent to Acts Church processing my personal information to coordinate rides and verify my identity. Required under POPIA.",
  'consent.guardian_text': 'I confirm that I am the parent or legal guardian of the child named in this booking and that I am legally competent to consent on their behalf. I consent to Acts Christian Church processing the minimum personal information needed to arrange this ride to a church event (the child\'s first name, the ride details and my own contact details), as described in the privacy notice (POPIA section 35). I understand a lone adult driver is never assigned a single unrelated minor and that I may withdraw this consent at any time.',
  'consent.why_we_ask': "Giving your details is voluntary, but without them we can't offer you a verified ride. You can withdraw consent or ask us to delete your information at any time.",
  'verify.rider_intro': "Just the basics for now. You'll do your ID check before your first ride.",
  'verify.driver_intro': "We check your ID and driver's licence before you can offer lifts. You only do this once.",
  'verify.pending_message': 'Your details look right. To finish the check, show the document to campus security or a campus admin.',
  'verify.manual_instructions': "A campus admin will check your documents in person. Once they're checked, you can offer lifts and book seats.",
  'matches.female_driver_note': 'A female driver is matched only with female riders or groups travelling together.',
  'matches.hidden_female_note': "{count} female driver(s) hidden. A female driver can't take only male passengers. Travelling with others? Go back and choose “A group together”.",
  'matches.empty': 'No drivers are offering this trip yet. Try other campuses or check again closer to the day.',
  'party.group_note': 'A female driver may drive a group travelling together, even if it includes men. For example, husband and wife, mother and daughter or adult siblings.',
  'booking.confirmation_note': "Be at the pick-up campus 10 minutes early. Look for the car and number plate shown above. Campus security checks the driver's ID at pick-up and drop-off.",
  'booking.minor_note': 'Under POPIA a rider under 18 is a child. We can only use their information with the prior consent of a parent or guardian (section 35).',
  'driver.safety_callout': "Safety matching. As a female driver you'll only be matched with female riders and groups travelling together. You'll never be matched with only male passengers.",
  'driver.bring_id_callout': 'At the campus. Bring your ID. Campus security checks it at pick-up and drop-off before riders travel.',
  'rideday.instructions': "Check that the car and number plate match the ones above. Campus security checks the driver's ID at pick-up and drop-off.",
  'security.instructions': "Check the driver's ID against the name and number plate below. Then tap the pick-up or drop-off button.",
  'maintenance.message': 'Carpool is taking a short break for maintenance. Please check back soon.',
  'login.intro': 'Sign in with your Acts Church account or your Planning Centre login.',
  'login.planning_centre_button': 'Continue with Planning Centre',
  'login.planning_centre_note': 'Use the same login you use for Church Center or Planning Centre. First time? Your Carpool account is created for you.',
  // 254f — optional profile photo (the member's CCMS account avatar)
  'photo.intro': "Add a clear photo of your face so the people you ride with and campus security can recognise you. It's optional.",
  'photo.privacy_note': 'This is your Acts Church profile photo, so changing it here updates it in other church apps too. In Carpool only people you ride with, campus security and the Carpool team see it.',
  'photo.driver_nudge': 'Drivers with a photo get booked more. Riders like to know who to look for.',
  'photo.driver_required': 'Please add a profile photo before you offer a lift. Riders and campus security use it to recognise you.',
  // 254g — document uploads, driver standards, emergency contacts, child-safety clearance
  'docs.intro': 'Upload clear photos of the documents below so the Carpool team can check them privately. It can save you a trip to campus security.',
  'docs.privacy_note': 'Your documents are stored privately and encrypted. Only the Carpool team can open them. We keep a record of every view. Each file is deleted {retentionDays} days after it has been checked. Other riders and drivers never see them.',
  'docs.required': 'Before you can continue, please upload: {documents}.',
  'docs.approval_pending': "Thanks! The Carpool team will check your documents soon. You can continue once they're accepted.",
  'docs.rejected': "We couldn't accept this document: {reason} Please upload a new one.",
  'docs.clearance_consent': 'I agree that Acts Church may process this clearance (special personal information under POPIA) only to decide whether I may drive under-18 passengers, and delete it after the check.',
  'driver.declaration_title': 'Driver declaration',
  'driver.declaration': `<p>Before you offer a lift, please confirm that:</p>
<ul>
<li>I hold a valid driver's licence for the vehicle I drive and will carry it on every ride.</li>
<li>The vehicle is licensed (valid licence disc), roadworthy and, to the best of my knowledge, insured. I will tell the Carpool team if that changes.</li>
<li>I will not drive under the influence of alcohol, drugs or medication that affects driving, or when I am too tired to drive safely.</li>
<li>I will obey the rules of the road, not use a hand-held phone while driving and make sure everyone wears a seatbelt.</li>
<li>I will carry no more passengers than there are seatbelts, and a child under 3 travels only in a suitable child seat (brought by the parent or guardian).</li>
<li>I am volunteering: I will not charge for a lift or ask for payment. A rider may choose to help with fuel, but I will never require it.</li>
<li>I will treat every passenger with respect, follow the church's safeguarding guidelines and report any incident to the Carpool team.</li>
</ul>
<p>Acts Church may suspend my driver profile if any of this stops being true.</p>`,
  'driver.declaration_required': 'Please read and accept the driver declaration before you offer a lift.',
  'licence.experience_required': 'Carpool drivers need at least {years} years of driving experience.',
  'emergency.intro': "Who should we contact if something happens on a ride? We only use this in an emergency. Please let them know you've shared their details.",
  'emergency.required': 'Please add an emergency contact before you continue.',
  'matches.hidden_clearance_note': '{count} driver(s) hidden. Only drivers with a child-safety clearance can take passengers under 18.',
  'booking.clearance_required': "This driver can't take under-18 passengers yet. Please choose another driver.",
  // ---- UI: shell + public pages
  'about.text': 'Acts Church Carpool helps members share lifts to church services and events.',
  'shell.email_banner': 'Please confirm your email address. We sent a link to {email}.',
  'shell.email_banner_sent': "We've sent the link again to {email}. Check your inbox and your spam folder.",
  'shell.email_banner_error': "We couldn't send the email just now. Please try again.",
  'shell.install_title': 'Add Carpool to your home screen',
  'shell.install_android': 'Install it like an app. It opens faster and can send you ride-day alerts.',
  'shell.install_ios': 'Tap the Share button, then “Add to Home Screen”. Carpool then opens like an app.',
  'landing.signin_prompt': 'Already have an account?',
  'landing.closed': 'New sign-ups are closed for now. Please check back soon.',
  'register.phone_help': 'Campus security may call you on the day. Other members never see it.',
  'register.email_help': "We'll send a link to this address to check that it's yours.",
  'forgot.intro': "Enter the email address you use for Carpool. We'll send you a link to choose a new password.",
  'forgot.sent': 'If we have an account for that email, a reset link is on its way. Check your inbox and your spam folder.',
  'reset.intro': 'Choose a new password for {email}.',
  'reset.invalid': 'This link has expired or has already been used. Ask for a new one and try again.',
  'reset.done': 'Your password is changed. Taking you to sign in…',
  'confirm.sent': "We've sent a link to {email}. Tap the link in that email to finish setting up your account.",
  'confirm.spam': "Can't find it after a minute? Check your spam folder or send it again.",
  'confirm.done': 'Your email address is confirmed. Taking you to sign in…',
  'confirm.invalid': 'This link has expired or has already been used.',
  'pc.signing_in': 'Signing you in with Planning Centre…',
  'pc.link_intro': 'You already have an Acts Church account for {email}. Enter its password once to link it to Planning Centre. After that, you can sign in with Planning Centre.',
  'profile_gate.intro': 'Carpool needs three details to match you safely and to reach you on the day. We save them on your Acts Church member profile.',
  'profile_gate.dob_help': 'We use this for the safety rules for under-18s.',
  'profile_gate.sex_help': 'As on your ID. We only use this for safety matching.',
  'profile_gate.phone_help': 'Campus security may call you on the day. Other members never see it.',
  'notfound.message': 'The link may be old, or the page has moved.',
  'privacy.intro': 'How Acts Church looks after the personal information Carpool needs.',
  'privacy.summary': "We only use your details to arrange safe lifts.\nWe never store your ID, passport or licence number as text. If you upload a copy of a document, only the Carpool team can open it.\nOther members never see your phone number.\nYour information is encrypted and stored in South Africa.\nYou can ask us to see, correct or delete your information at any time.",
  'terms.intro': 'The rules for riders and drivers who share lifts through Carpool.',
  'terms.summary': "Lifts are free. Drivers are church members who volunteer.\nYou must be 18 or older to sign up. A parent or guardian can book for a child.\nBring your ID. Campus security may check it at pick-up and drop-off.\nBe on time and cancel early if your plans change.\nTreat everyone with respect.",
  'terms.missing': "The terms of use aren't published yet. Please check back soon.",
  // ---- UI: rider + driver flows
  'booking.minor_group_rule': 'A child must travel with at least one other person in the group. We never place one child alone with an unrelated adult driver.',
  'booking.child_name_required': 'Please enter the name of the child who is travelling.',
  'rideday.arrival_too_early': "It's too early to tell your riders you've arrived. You can do this from {time}.",
  'rideday.arrival_too_late': "It's too late to tell your riders you've arrived. The departure time has passed.",
  'ride.event_intro': "Choose the service or event you're going to.",
  'ride.events_empty': "We'll show services and events here as soon as they open for lifts. Please check again soon.",
  'ride.from_intro': 'Choose the Acts Church campus where your driver will pick you up.',
  'ride.to_intro': 'Choose the campus where the service or event is.',
  'ride.service_intro': 'Each driver goes to one service. Choose yours, or see every driver going to {campus}.',
  'ride.who_intro': 'This tells the driver how many seats you need.',
  'ride.sex_intro': 'We use this for our safety matching rules.',
  'ride.group_intro': 'Count everyone who is travelling, including you.',
  'ride.child_group_intro': 'Count everyone who is travelling, including your child.',
  'ride.minor_member_note': "You're under {minorAge}, so a parent or guardian needs to agree to this lift and do an ID check.",
  'ride.matches_intro': 'Every driver here has had an ID and licence check. Choose the one that suits you.',
  'matches.hidden_female_one': "1 female driver is hidden. A female driver can't take only male passengers.",
  'matches.hidden_female_many': "{count} female drivers are hidden. A female driver can't take only male passengers.",
  'matches.hidden_male_one': '1 male driver is hidden. A male driver only takes groups that include a man.',
  'matches.hidden_male_many': '{count} male drivers are hidden. A male driver only takes groups that include a man.',
  'matches.hidden_seats_one': "1 driver doesn't have enough free seats for your group.",
  'matches.hidden_seats_many': "{count} drivers don't have enough free seats for your group.",
  'matches.hidden_clearance_one': '1 driver is hidden. Only drivers with a child-safety clearance can take passengers under 18.',
  'matches.hidden_clearance_many': '{count} drivers are hidden. Only drivers with a child-safety clearance can take passengers under 18.',
  'matches.other_routes': 'These trips have drivers for the same service. Tap one to see them.',
  'ride.id_intro': "We check every rider's ID once, so drivers know who is getting in. You won't need to do this again.",
  'ride.book_intro': 'Check the details, then book your seat.',
  'ride.child_intro': 'Only your driver and campus security see this name. They use it to know who to look for.',
  'ride.guardian_intro': "You're booking for a child, so we need a parent or guardian's details. We only use them for this lift.",
  'ride.guardian_id_intro': "We check the parent or guardian's ID once. It helps keep every child who travels safe.",
  'ride.guardian_consent_intro': 'Please read this and agree before you book for your child.',
  'ride.confirm_lede': '{driver} will drive you to {event}.',
  'ride.confirm_lede_child': '{driver} will drive {child} to {event}.',
  'rideday.cancelled_by_driver': "Your driver had to cancel this lift. We're sorry. You can look for another lift for the same trip.",
  'rideday.cancelled_by_you': 'You cancelled this seat. Your driver has been told.',
  'rideday.completed': 'This lift is over. Thank you for travelling with Carpool.',
  'rideday.no_show': "This booking was marked as a no-show. If that's not right, please report a problem.",
  'rideday.waiting': "We'll show an update here as soon as your driver arrives.",
  'rideday.driver_here': '{driver} is at {campus}. Go to the meeting point and check the number plate.',
  'rideday.cancel_help': 'Your driver will be told straight away, so someone else can take the seat.',
  'rideday.cancel_cutoff': "You can't cancel in the app within {cutoff} of leaving. If you can't make it, please tell your driver at the campus.",
  'rideday.help': "Can't find your driver, or did something go wrong? Let the Carpool team know.",
  'report.intro': 'Tell us what happened. The Carpool team will look into it and get back to you.',
  'report.emergency': 'If anyone is in danger right now, call 10111. From a cellphone you can also call 112.',
  'report.sent': "Thank you. Your report is with the Carpool team. They'll get back to you soon.",
  'rideday.no_riders': "Riders going to this service can see your free seats. We'll let you know when someone books.",
  'rideday.arrived_help': "Tap this when you get to {campus}. We'll tell your riders you're there.",
  'rideday.arrived_confirm': "We'll send your riders a message to say you're at {campus}.",
  'rideday.arrived_done': 'You told your riders you arrived at {time}.',
  'rideday.driver_cancel_riders': 'Your riders will be told straight away, so they can find another lift.',
  'rideday.driver_cancel_none': 'Nobody has booked yet. Your lift will be closed.',
  'rideday.driver_cancelled': 'You cancelled this lift. Your riders have been told.',
  'rideday.driver_completed': 'This lift is over. Thank you for driving.',
  'drive.ready_intro': 'Before riders can book your lift, we need a few things from you. You only do this once.',
  'drive.ready_new': "It takes about 10 minutes. Please have your ID and driver's licence with you.",
  'drive.ready_done': 'Riders can book your lifts. Tap an item to change your details.',
  'drive.ready_waiting': "Please show your ID and driver's licence to campus security or a campus admin. You can offer lifts once they're checked.",
  'drive.ready_todo': "Riders can't see your lifts yet. Please finish the steps marked “To do”.",
  'drive.you_intro': 'We use this for our safety matching rules.',
  'drive.car_intro': 'Riders use this to find your car at the campus.',
  'drive.licence_intro': "We check your licence once. We don't keep the licence number.",
  'drive.promise_intro': 'Please read this and agree before you offer lifts.',
  'drive.id_intro': 'We check your ID once, before your first lift.',
  'drive.licence_check_intro': "Your last licence check didn't go through. Please enter the details exactly as they are on your card.",
  'drive.docs_optional': "You can add these if you like. You don't need them to offer lifts.",
  'drive.events_intro': "Choose every service you're driving to. You'll choose the route next.",
  'drive.from_intro': 'Riders will meet you at this campus.',
  'drive.to_intro': 'Choose the campus where the service or event is.',
  'drive.service_intro': 'Riders going to this service at {campus} will see your lift.',
  'drive.details_intro': 'Tell riders how many seats you have and when you leave.',
  'drive.default_time': "That's {minutes} minutes before the service starts.",
  'drive.default_time_each': "If you don't choose a time, we'll use {minutes} minutes before each service or event starts.",
  'drive.review_intro': 'Tap Change to fix anything before you offer it.',
  'drive.review_next': "Riders going to these services can book your free seats.\nWe'll let you know when someone books.\nOn the day, tap “I've arrived” when you get to the campus.",
  'drive.review_waiting': "You can offer this lift once your ID and licence checks are done. Your choices are saved, so you can come back later.",
  'drive.listed_ready': "Riders can book your seats now. We'll let you know when someone books.",
  'drive.lifts_intro': "Tap a lift to see who's booked, say you've arrived or cancel.",
  'drive.lifts_empty': "Choose the services you're driving to. Riders can then book your free seats.",
  'licence.experience_wait': 'You can offer lifts from {year}. Carpool drivers need at least {years} years of driving experience.',
  'ride.book_tip': "Be at the pick-up campus 10 minutes early. Campus security checks the driver's ID at pick-up and drop-off.",
  'ride.child_who_intro': 'Tell us if your child is travelling on their own or with others.',
  'drive.events_todo_one': 'There is 1 thing to do on your checklist before you can offer a lift. You can choose your services now.',
  'drive.events_todo_many': 'There are {count} things to do on your checklist before you can offer a lift. You can choose your services now.',
  'drive.events_waiting': "You can choose your services now and offer the lift once your ID and licence are checked.",
  'ride.id_pending_block': "Your ID check isn't finished yet. Please show your ID to campus security or a campus admin. You can book once it's checked.",
  'ride.guardian_pending_block': "The parent or guardian's ID check isn't finished yet. Please show the ID to campus security or a campus admin. You can book once it's checked.",
  'drive.id_done_pending': 'A campus admin will finish the check when you show them your ID. You can offer lifts once your ID and licence are checked.',
  // ---- UI: home, account, security
  'account.identity_locked': "Your name, date of birth and sex are part of your ID check, so you can't change them here. If something is wrong, please contact the Carpool team.",
  'profile.sex_locked': "Your sex is part of your ID check, so you can't change it here. If it's wrong, please contact the Carpool team.",
  'home.lede_next': "Here's what's coming up.",
  'home.lede_empty': 'Nothing booked yet. Find a lift to a service or event, or share your spare seats.',
  'home.welcome_title': 'Where would you like to go?',
  'home.today_rider': 'Be at the pick-up point 10 minutes early and look for this car and number plate.',
  'home.today_driver': 'Your riders will look for your car and number plate. Please be at the pick-up point on time.',
  'home.closed': 'New bookings are closed for now. Your rides are still here.',
  'home.rider_closed': 'Booking new rides is closed for now.',
  'home.driver_closed': 'Offering new lifts is closed for now.',
  'home.maintenance': "Carpool is taking a short break, so you can't book or change rides right now. Your booked rides still stand.",
  'home.load_error': 'Please check your internet connection and try again. Your bookings are safe.',
  'home.list_error': "We couldn't load your other rides just now.",
  'home.checks_nudge': 'Riders can book your lifts once your ID and licence are checked.',
  'home.checks_failed': "One of your driver checks didn't pass. Please have a look.",
  'account.intro': 'Your details, documents and settings.',
  'account.details_note': 'Campus security and the Carpool team may call you on the day. Other members never see your cellphone number.',
  'account.name_help': 'Use the name on your ID. Your driver and campus security see it.',
  'account.name_locked': 'To change your name, please contact us.',
  'account.details_saved': 'Your details are saved.',
  'account.car_help': 'To change your car, seats or licence, go through your driver details again.',
  'account.car_contact': 'To change your car or licence, please contact us.',
  'account.suspended': "Your driver profile is on hold, so riders can't book your lifts. Please contact us.",
  'account.checks_intro': 'Campus security checks these in person. You only do this once.',
  'account.checks_error': "We couldn't load your ID checks just now.",
  'account.appearance_help': 'Choose how Carpool looks on this phone.',
  'account.consents_intro': 'Our record of the consents you gave us, and when.',
  'account.consents_empty': "Nothing yet. We'll ask when you first book or offer a lift.",
  'account.delete_text': "You can ask us to delete your Carpool information at any time. We'll do it once your rides are over and the law no longer asks us to keep the records.",
  'account.delete_note': 'Your request goes to {email}.',
  'checks.verified': 'Checked and confirmed.',
  'checks.pending': 'Waiting for a check. Show the document to campus security on your next visit.',
  'checks.failed': "The check didn't pass. Please check your details and try again.",
  'checks.expired': 'This document has expired. Please use a valid one.',
  'checks.revoked': 'This check was withdrawn. Please contact us.',
  'checks.id_todo': "We'll ask for your ID before your first ride.",
  'checks.licence_todo': "We'll ask for your licence before you offer a lift.",
  'checks.guardian_todo': "We'll ask for a parent or guardian's ID before a child's first ride.",
  'documents.missing': 'We still need: {documents}.',
  'documents.all_optional': 'Everything here is optional.',
  'documents.resize_note': 'Before uploading, your phone makes photos smaller and removes location details.',
  'documents.uploaded': 'Uploaded. Thank you.',
  'documents.uploaded_review': 'Uploaded. The Carpool team will check it soon.',
  'documents.accepted': 'Uploaded and accepted. Thank you.',
  'documents.deleted': 'Document deleted.',
  'documents.expired': 'This document has expired. Please upload a current one.',
  'documents.purged': 'Checked. We deleted the copy to protect your privacy.',
  'photo_card.resize_note': 'Before uploading, your phone makes the photo smaller and removes location details.',
  'emergency_card.optional': 'This is optional. You can leave it blank.',
  'emergency_card.saved': 'Your emergency contact is saved.',
  'alerts.intro': "We'll only tell you when your driver arrives or a ride changes.",
  'alerts.on': "Ride-day alerts are on for this phone. We'll tell you when your driver arrives or a ride changes.",
  'alerts.blocked': 'Alerts are blocked on this phone. To turn them on, allow notifications for Carpool in your phone or browser settings.',
  'security.choose_campus': 'Choose your campus to see which drivers are expected.',
  'security.empty': 'No lifts are expected at {campus} for this date and service.',
  'security.refresh_note': 'This list updates every 30 seconds.',
  'security.record_help': "Compare the document with the driver. Then record the result. We don't keep the document number.",
  'security.documents_note': "These are private copies. Every view is logged and each link stops working after a few minutes. Please don't screenshot or share them.",
} as const;

export type CopyKey = keyof typeof DEFAULT_COPY;

// ---------------------------------------------------------------- default settings

/** Seed defaults (254 + 254c + 254f + 254g). Used until the lookups query resolves and for any field an older API omits. */
export const DEFAULT_SETTINGS: RidesPublicSettingsView = {
  betaBannerText: null,
  privacyNoticeHtml: null,
  termsHtml: null,
  mirrorRuleEnabled: true,
  minorRequiresGroup: true,
  maxVehicleSeats: 6,
  bookingCutoffMinutes: 0,
  verificationProvider: RIDES_PROVIDERS.Manual,
  betaCampusLocationIDs: [],
  dataConsentTextVersion: '1',
  guardianConsentTextVersion: '1',
  publicBaseUrl: null,
  femaleDriverRuleEnabled: true,
  minorAgeYears: 18,
  driverMinimumAgeYears: 18,
  guardianMinimumAgeYears: 18,
  requireRiderIdVerification: true,
  requireDriverIdVerification: true,
  requireDriverLicenceVerification: true,
  allowPassport: true,
  allowForeignLicence: true,
  foreignLicenceNeedsEnglishOrIdp: true,
  allowedLicenceCodes: 'B,EB,C1,EC1,C,EC',
  verificationValidityDays: 0,
  minVehicleSeats: 1,
  defaultDepartureLeadMinutes: 45,
  eventWindowDays: 120,
  maxOpenListingsPerDriver: 20,
  allowDriverNotes: true,
  requireServiceTimeForSundays: true,
  listingCutoffMinutes: 0,
  maxPartySize: 6,
  riderCancellationCutoffMinutes: 0,
  autoCompleteAfterHours: 6,
  riderRegistrationOpen: true,
  driverRegistrationOpen: true,
  securityViewEnabled: true,
  pushOptInEnabled: true,
  installHintEnabled: true,
  planningCentreLoginEnabled: true,
  maintenanceMode: false,
  betaMode: true,
  guardianRelationships: 'Parent,Legal guardian',
  supportEmail: 'rides@actschurch.co.za',
  supportPhone: null,
  deleteDataEmail: 'privacy@actschurch.co.za',
  // 254f — profile photos
  photosEnabled: true,
  showDriverPhotoToRiders: true,
  showRiderPhotoToDrivers: true,
  showPhotosToSecurity: true,
  showMinorRiderPhotos: false,
  driverPhotoRequired: false,
  photoMaxMb: 5,
  // 254g — document uploads + safeguarding / driver standards
  documentUploadsEnabled: true,
  // Server-derived (is private storage configured?). Only an explicit false hides uploads here;
  // GET me/documents (enabled / storageReady) has the final say before anything is shown.
  documentStorageReady: true,
  documentApprovalRequired: false,
  documentRetentionDays: 30,
  unreviewedDocumentRetentionDays: 180,
  documentMaxMb: 8,
  documentLinkMinutes: 5,
  showDocumentsToSecurity: false,
  minorRidesRequireClearedDriver: false,
  driverDeclarationRequired: true,
  driverDeclarationTextVersion: '2026-10-10',
  minimumYearsLicensed: 0,
  driverEmergencyContactRequirementID: RIDES_REQUIREMENT_LEVEL.NotAsked,
  riderEmergencyContactRequirementID: RIDES_REQUIREMENT_LEVEL.NotAsked,
  showEmergencyContactToSecurity: true,
  copy: {},

};

/** Merge the API settings over the defaults — nulls/undefined never clobber a default. */
export function mergeSettings(api: Partial<RidesPublicSettingsView> | null | undefined): RidesPublicSettingsView {
  if (!api) return DEFAULT_SETTINGS;
  const out = { ...DEFAULT_SETTINGS } as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(api)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '' && k !== 'allowedLicenceCodes' && k !== 'guardianRelationships') continue;
    out[k] = v;
  }
  out.copy = { ...(api.copy ?? {}) };
  return out as unknown as RidesPublicSettingsView;
}

export interface RidesSettingsResult {
  settings: RidesPublicSettingsView;
  /** True once the API has answered (even if it failed and we are on defaults). */
  isSettled: boolean;
  /** True while the first lookups request is in flight. */
  isLoading: boolean;
  /** True when `settings` came from the API rather than the defaults. */
  isFromApi: boolean;
}

/**
 * Public settings + copy from the ANONYMOUS endpoint (GET rides/api/rides/public/settings), so the
 * pages people see before signing in (landing, sign-in, register, privacy, terms, maintenance) are
 * settings-driven too. 5-min stale; merged over the typed defaults.
 */
export function useRidesSettings(): RidesSettingsResult {
  const lookups = useRidesLookupsService();
  const { data, isLoading, isFetched } = useQuery<RidesPublicSettingsView>({
    queryKey: ridesKeys.publicSettings,
    queryFn: () => lookups.getPublicSettings(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const settings = useMemo(() => mergeSettings(data), [data]);
  return { settings, isSettled: isFetched, isLoading, isFromApi: !!data };
}

// ---------------------------------------------------------------- copy

export type CopyVars = Record<string, string | number | null | undefined>;

/** Replace `{name}` placeholders; unknown placeholders are left as-is. */
export function fillPlaceholders(text: string, vars?: CopyVars): string {
  if (!vars) return text;
  return text.replace(/\{([a-zA-Z0-9_.]+)\}/g, (m, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? m : String(v);
  });
}

/** Resolve a copy key against the API copy map, then the fallback, then the seed default. */
export function resolveCopy(copy: Record<string, string> | undefined, key: string, fallback?: string, vars?: CopyVars): string {
  const fromApi = copy?.[key];
  const raw = (fromApi && fromApi.trim()) || fallback || (DEFAULT_COPY as Record<string, string>)[key] || key;
  return fillPlaceholders(raw, vars);
}

/**
 * `t(key, fallback?, vars?)` — public portal text. Reads `settings.copy[key]`, falls back to
 * the seed default in DEFAULT_COPY (or the explicit fallback) and fills `{name}` placeholders.
 */
export function useCopy() {
  const { settings } = useRidesSettings();
  const copy = settings.copy;
  const t = useCallback(
    (key: CopyKey | (string & {}), fallback?: string, vars?: CopyVars) => resolveCopy(copy, key, fallback, vars),
    [copy],
  );
  return { t, settings };
}

// ---------------------------------------------------------------- derived helpers

export interface PhotoRules {
  /** Members may add / see profile photos at all. */
  enabled: boolean;
  /** Riders see the driver's photo (match cards, booking, ride day, Home). */
  driverToRiders: boolean;
  /** Drivers see their riders' photos (driver ride day). */
  riderToDrivers: boolean;
  /** Campus security sees driver + rider photos. */
  security: boolean;
  /** A minor rider's photo (forMinor bookings) is shown to the driver and security. */
  minors: boolean;
  /** A driver must have a photo before listing a ride. */
  driverRequired: boolean;
  /** Upper bound for the prepared (resized) photo. */
  maxMb: number;
}

/**
 * Who may see photos, from the 254f settings. The API already nulls photo URLs a viewer may not
 * see; the portal applies the same switches so a cached view never shows a photo after an admin
 * turns them off.
 */
export function photoRules(settings: RidesPublicSettingsView): PhotoRules {
  const on = settings.photosEnabled !== false;
  return {
    enabled: on,
    driverToRiders: on && settings.showDriverPhotoToRiders !== false,
    riderToDrivers: on && settings.showRiderPhotoToDrivers !== false,
    security: on && settings.showPhotosToSecurity !== false,
    minors: on && settings.showMinorRiderPhotos === true,
    driverRequired: on && settings.driverPhotoRequired === true,
    maxMb: settings.photoMaxMb > 0 ? settings.photoMaxMb : DEFAULT_SETTINGS.photoMaxMb,
  };
}

/** A photo URL the viewer may see, or null (blank / whitespace / switched off). */
export function visiblePhoto(url: string | null | undefined, allowed: boolean): string | null {
  if (!allowed || !url) return null;
  const trimmed = url.trim();
  return trimmed ? trimmed : null;
}

export interface DocumentRules {
  /** The Documents area is shown at all (uploads switched on AND private storage configured). */
  enabled: boolean;
  /** Required documents count only once a reviewer accepts them (not just uploaded). */
  approvalRequired: boolean;
  /** Upper bound for one prepared file (clamped 1–20 MB like the server). */
  maxMb: number;
  /** Days after review before a file is deleted (privacy note). */
  retentionDays: number;
  /** Minutes a view link stays valid. */
  linkMinutes: number;
  /** Under-18 bookings only match drivers with an accepted child-safety clearance. */
  minorRidesRequireClearedDriver: boolean;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Document-upload switches from the 254g settings. Hide everything unless `enabled`. */
export function documentRules(settings: RidesPublicSettingsView): DocumentRules {
  const maxMb = settings.documentMaxMb && settings.documentMaxMb > 0 ? settings.documentMaxMb : DEFAULT_SETTINGS.documentMaxMb ?? 8;
  const link = settings.documentLinkMinutes && settings.documentLinkMinutes > 0 ? settings.documentLinkMinutes : DEFAULT_SETTINGS.documentLinkMinutes ?? 5;
  return {
    enabled: settings.documentUploadsEnabled !== false && settings.documentStorageReady !== false,
    approvalRequired: settings.documentApprovalRequired === true,
    maxMb: clamp(maxMb, 1, 20),
    retentionDays: Math.max(0, settings.documentRetentionDays ?? DEFAULT_SETTINGS.documentRetentionDays ?? 30),
    linkMinutes: clamp(link, 1, 60),
    minorRidesRequireClearedDriver: settings.minorRidesRequireClearedDriver === true,
  };
}

export type RequirementLevel = 'notAsked' | 'optional' | 'required';

/** RIDES_REQUIREMENT_LEVEL id → 'notAsked' | 'optional' | 'required' (unknown ids = not asked). */
export function requirementLevel(id: number | null | undefined): RequirementLevel {
  if (id === RIDES_REQUIREMENT_LEVEL.Required) return 'required';
  if (id === RIDES_REQUIREMENT_LEVEL.Optional) return 'optional';
  return 'notAsked';
}

/** The stronger of two levels (a member who is both rider and driver sees the stricter one). */
export function strongerLevel(a: RequirementLevel, b: RequirementLevel): RequirementLevel {
  const rank: Record<RequirementLevel, number> = { notAsked: 0, optional: 1, required: 2 };
  return rank[a] >= rank[b] ? a : b;
}

export interface DriverStandards {
  /** Drivers accept the declaration (current `declarationTextVersion`) before listing. */
  declarationRequired: boolean;
  declarationTextVersion: string;
  /** 0 = don't ask for the year the licence was first issued. */
  minimumYearsLicensed: number;
  /** Emergency contact asked of drivers / riders. */
  driverEmergency: RequirementLevel;
  riderEmergency: RequirementLevel;
}

/** Driver-standard + emergency-contact switches from the 254g settings. */
export function driverStandards(settings: RidesPublicSettingsView): DriverStandards {
  return {
    declarationRequired: settings.driverDeclarationRequired !== false,
    declarationTextVersion: settings.driverDeclarationTextVersion?.trim() || DEFAULT_SETTINGS.driverDeclarationTextVersion || '1',
    minimumYearsLicensed: Math.max(0, settings.minimumYearsLicensed ?? 0),
    driverEmergency: requirementLevel(settings.driverEmergencyContactRequirementID),
    riderEmergency: requirementLevel(settings.riderEmergencyContactRequirementID),
  };
}

/** "B,EB,C1" → ["B","EB","C1"] (upper-cased, trimmed, empty entries dropped). */
export function csvList(value: string | null | undefined, upper = false): string[] {
  return (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (upper ? s.toUpperCase() : s));
}

export function allowedLicenceCodeList(settings: RidesPublicSettingsView): string[] {
  const list = csvList(settings.allowedLicenceCodes, true);
  return list.length ? list : csvList(DEFAULT_SETTINGS.allowedLicenceCodes, true);
}

export function guardianRelationshipList(settings: RidesPublicSettingsView): string[] {
  const list = csvList(settings.guardianRelationships);
  return list.length ? list : csvList(DEFAULT_SETTINGS.guardianRelationships);
}

/** Inclusive seat range for the seat chips (min..max, always at least [1]). */
export function seatRange(settings: RidesPublicSettingsView, cap?: number | null): number[] {
  const min = Math.max(1, settings.minVehicleSeats || 1);
  let max = Math.max(min, settings.maxVehicleSeats || min);
  if (cap && cap >= min) max = Math.min(max, cap);
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

/** Vars available to every legal/contact string (privacy + terms HTML, support links). */
export function contactVars(settings: RidesPublicSettingsView, appName: string): CopyVars {
  return {
    appName,
    deleteDataEmail: settings.deleteDataEmail || DEFAULT_SETTINGS.deleteDataEmail,
    supportEmail: settings.supportEmail || DEFAULT_SETTINGS.supportEmail,
    supportPhone: settings.supportPhone || '',
  };
}

/** mailto: for a POPIA deletion request addressed to `deleteDataEmail`. */
export function deleteDataMailto(settings: RidesPublicSettingsView, appName: string): string {
  const to = settings.deleteDataEmail || DEFAULT_SETTINGS.deleteDataEmail || '';
  return (
    `mailto:${to}?subject=` +
    encodeURIComponent(`${appName}: please delete my data`) +
    '&body=' +
    encodeURIComponent(
      `Hello,\n\nPlease delete (or de-identify) my ${appName} information once any active ride and legal retention period has ended.\n\nName:\nEmail used for Carpool:\n\nThank you.`,
    )
  );
}

/** Support contact: email first, then phone. */
export function supportLink(settings: RidesPublicSettingsView): { href: string; label: string } | null {
  const email = settings.supportEmail?.trim();
  if (email) return { href: `mailto:${email}`, label: 'Support' };
  const phone = settings.supportPhone?.trim();
  if (phone) return { href: `tel:${phone.replace(/\s+/g, '')}`, label: 'Support' };
  return null;
}
